import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus } from 'lucide-react';
import { Section } from '../FormFields';
import { assignRegionOwner } from '../../../utils/regionOwner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RegionSettingsPanel = ({ role = 'admin', onOwnerChanged }) => {
  const [regions, setRegions] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copyOpenFor, setCopyOpenFor] = useState(null);
  const [copyTargets, setCopyTargets] = useState([]);
  const [copying, setCopying] = useState(false);
  const [simOpenFor, setSimOpenFor] = useState(null);
  const [simDraft, setSimDraft] = useState({ auto_assign: false, voucher_required: false });
  const [simManual, setSimManual] = useState({ name: '', email: '', voucher_code: '' });
  const [simResult, setSimResult] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [suggestingKey, setSuggestingKey] = useState(null);

  // Same "already owns >=1 other region" proxy region_health uses server-side
  // for its Fix-It suggestions — computed here from the managers list this
  // panel already fetches, so Auto-assign never turns ON without a nudge
  // toward a real owner.
  const suggestedCandidatesFor = (region) =>
    managers.filter((m) => !(m.regions || []).includes(region) && (m.owns_regions || []).length > 0).slice(0, 3);

  const handleMakeOwner = async (candidate, region) => {
    const key = `${candidate.username}-${region}`;
    setSuggestingKey(key);
    try {
      await assignRegionOwner(candidate, region);
      toast.success(`${candidate.name} is now the owner of this region`);
      fetchAll();
      onOwnerChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign owner');
    } finally {
      setSuggestingKey(null);
    }
  };

  const fetchAll = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [regionsRes, teamRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/region-settings`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/team-accounts`, { withCredentials: true }),
      ]);
      setRegions(regionsRes.data.regions || []);
      setManagers((teamRes.data.members || []).filter((m) => m.role === 'manager'));
    } catch (err) {
      if (!silent) toast.error('Failed to load region settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // Owner/auto-assign/voucher-required can all be changed from a
    // different tab or session (e.g. Team Accounts) — poll silently so the
    // alert and dropdowns here don't go stale without a manual reload.
    const interval = setInterval(() => fetchAll(true), 60000);
    return () => clearInterval(interval);
  }, []);

  const toggleFlag = async (region, field, value) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/region-settings/${region}`, { [field]: value }, { withCredentials: true });
      setRegions((prev) => prev.map((r) => (r.region === region ? { ...r, [field]: value } : r)));
      toast.success('Region settings updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update region settings');
    }
  };

  const setOwner = async (region, username) => {
    try {
      if (username) {
        await axios.put(`${BACKEND_URL}/api/admin/team-accounts/${username}/region-owner`, { region, is_owner: true }, { withCredentials: true });
      } else {
        const current = regions.find((r) => r.region === region);
        if (current?.owner_username) {
          const outgoingUsername = current.owner_username;
          const outgoingName = current.owner_name || outgoingUsername;
          await axios.put(`${BACKEND_URL}/api/admin/team-accounts/${outgoingUsername}/region-owner`, { region, is_owner: false }, { withCredentials: true });
          // Ownership and region *scope* are separate grants — ask before also
          // revoking scope, since a manager may have been assigned here for
          // reasons beyond just being the owner.
          if (window.confirm(`Ownership of ${current.name} was removed. Also revoke ${outgoingName}'s access to this region? They'll no longer be able to see or manage leads here.`)) {
            const outgoingManager = managers.find((m) => m.username === outgoingUsername);
            if (outgoingManager) {
              const trimmedRegions = (outgoingManager.regions || []).filter((r) => r !== region);
              await axios.put(`${BACKEND_URL}/api/admin/team-accounts/${outgoingUsername}`, { regions: trimmedRegions }, { withCredentials: true });
            }
          }
        }
      }
      toast.success('Region owner updated');
      fetchAll();
      onOwnerChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set region owner');
    }
  };

  const toggleCopyTarget = (region) => {
    setCopyTargets((prev) => (prev.includes(region) ? prev.filter((r) => r !== region) : [...prev, region]));
  };

  const applyCopy = async (source) => {
    if (copyTargets.length === 0) return;
    setCopying(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/region-settings/${source}/copy-to`, { targets: copyTargets }, { withCredentials: true });
      const nameOf = (code) => regions.find((r) => r.region === code)?.name || code;
      const skipped = res.data.owner_skipped || [];
      toast.success(`Copied ${nameOf(source)}'s settings to ${copyTargets.length} region(s)`);
      if (skipped.length > 0) {
        toast(`Owner not copied to ${skipped.map(nameOf).join(', ')} — that manager isn't assigned to those regions yet`);
      }
      setCopyOpenFor(null);
      setCopyTargets([]);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to copy region settings');
    } finally {
      setCopying(false);
    }
  };

  const openSimulate = (r) => {
    setSimOpenFor(simOpenFor === r.region ? null : r.region);
    setSimDraft({ auto_assign: r.auto_assign, voucher_required: r.voucher_required });
    setSimManual({ name: '', email: '', voucher_code: '' });
    setSimResult(null);
  };

  const runSimulation = async (region) => {
    setSimulating(true);
    try {
      const payload = {
        auto_assign: simDraft.auto_assign,
        voucher_required: simDraft.voucher_required,
        manual_test: simManual.name.trim() && simManual.email.trim()
          ? { name: simManual.name.trim(), email: simManual.email.trim(), voucher_code: simManual.voucher_code.trim() || null }
          : null,
      };
      const res = await axios.post(`${BACKEND_URL}/api/admin/region-settings/${region}/simulate`, payload, { withCredentials: true });
      setSimResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to run simulation');
    } finally {
      setSimulating(false);
    }
  };

  if (loading) return <p className="text-sm text-slate-400 italic">Loading region settings…</p>;

  return (
    <Section
      title="Region Settings"
      description="Configure automatic lead assignment per region. When 'Auto-assign' is ON and a Region Owner is set, every new inbound lead in that region is assigned to them immediately — unless 'Voucher required' is also ON, in which case it's left for manual review."
    >
      <div className="space-y-3" data-testid="region-settings-panel">
        {regions.length === 0 && <p className="text-sm text-slate-400 italic">No regions registered yet.</p>}
        {regions.map((r) => {
          const suggestions = role === 'admin' && r.auto_assign && !r.owner_username ? suggestedCandidatesFor(r.region) : [];
          return (
            <div key={r.region} className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-4 bg-slate-50 border border-slate-200 rounded-xl p-3" data-testid={`region-settings-row-${r.region}`}>
                <span className="font-medium text-sm text-slate-800 w-28 flex-shrink-0">{r.name}</span>
                <label className="flex items-center gap-2 text-xs text-slate-600" htmlFor={`region-auto-assign-${r.region}`}>
                  <input
                    id={`region-auto-assign-${r.region}`}
                    type="checkbox"
                    checked={!!r.auto_assign}
                    onChange={(e) => toggleFlag(r.region, 'auto_assign', e.target.checked)}
                    data-testid={`region-auto-assign-${r.region}`}
                  />
                  Auto-assign to owner
                </label>
                <label className="flex items-center gap-2 text-xs text-slate-600" htmlFor={`region-voucher-required-${r.region}`}>
                  <input
                    id={`region-voucher-required-${r.region}`}
                    type="checkbox"
                    checked={!!r.voucher_required}
                    onChange={(e) => toggleFlag(r.region, 'voucher_required', e.target.checked)}
                    data-testid={`region-voucher-required-${r.region}`}
                  />
                  Voucher required
                </label>
                {role === 'admin' ? (
                  <select
                    value={r.owner_username || ''}
                    onChange={(e) => setOwner(r.region, e.target.value)}
                    data-testid={`region-owner-select-${r.region}`}
                    className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white flex-1 min-w-[160px]"
                  >
                    <option value="">No owner</option>
                    {managers
                      .filter((m) => (m.regions || []).includes(r.region) || m.username === r.owner_username)
                      .map((m) => (
                        <option key={m.username} value={m.username}>{m.name}</option>
                      ))}
                  </select>
                ) : (
                  <span className="text-xs text-slate-400" data-testid={`region-owner-readonly-${r.region}`}>
                    Owner: {r.owner_name || 'None'}
                  </span>
                )}
                {role === 'admin' && regions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => { setCopyOpenFor(copyOpenFor === r.region ? null : r.region); setCopyTargets([]); }}
                    data-testid={`region-copy-toggle-${r.region}`}
                    className="text-xs text-indigo-600 hover:text-indigo-800 underline flex-shrink-0"
                  >
                    Copy to…
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => openSimulate(r)}
                  data-testid={`region-simulate-toggle-${r.region}`}
                  className="text-xs text-teal-600 hover:text-teal-800 underline flex-shrink-0"
                >
                  Simulate…
                </button>
              </div>
              {suggestions.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 pl-2" data-testid={`region-settings-suggestions-${r.region}`}>
                  <span className="text-[11px] text-slate-400">Auto-assign is ON with no owner yet —</span>
                  {suggestions.map((c) => {
                    const key = `${c.username}-${r.region}`;
                    return (
                      <button
                        key={c.username}
                        type="button"
                        onClick={() => handleMakeOwner(c, r.region)}
                        disabled={suggestingKey === key}
                        data-testid={`region-settings-suggest-${r.region}-${c.username}`}
                        title={`${c.name} already manages ${c.regions.length} other region(s)`}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-white border border-emerald-300 rounded-full px-2 py-0.5 hover:bg-emerald-50 disabled:opacity-50 transition-colors"
                      >
                        <UserPlus className="w-3 h-3" /> {suggestingKey === key ? 'Assigning…' : `Make ${c.name} owner`}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {simOpenFor && (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-3 space-y-3" data-testid="region-simulate-panel">
            <p className="text-xs font-medium text-teal-800">
              Preview how {regions.find((r) => r.region === simOpenFor)?.name || simOpenFor}'s rules WOULD assign leads — nothing is saved or redeemed.
            </p>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={simDraft.auto_assign}
                  onChange={(e) => setSimDraft((d) => ({ ...d, auto_assign: e.target.checked }))}
                  data-testid="region-simulate-draft-auto-assign"
                />
                Auto-assign to owner
              </label>
              <label className="flex items-center gap-2 text-xs text-slate-700">
                <input
                  type="checkbox"
                  checked={simDraft.voucher_required}
                  onChange={(e) => setSimDraft((d) => ({ ...d, voucher_required: e.target.checked }))}
                  data-testid="region-simulate-draft-voucher-required"
                />
                Voucher required
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              <input
                value={simManual.name}
                onChange={(e) => setSimManual((m) => ({ ...m, name: e.target.value }))}
                placeholder="Test lead name (optional)"
                data-testid="region-simulate-manual-name"
                className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 flex-1 min-w-[120px]"
              />
              <input
                value={simManual.email}
                onChange={(e) => setSimManual((m) => ({ ...m, email: e.target.value }))}
                placeholder="Test lead email (optional)"
                data-testid="region-simulate-manual-email"
                className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 flex-1 min-w-[140px]"
              />
              <input
                value={simManual.voucher_code}
                onChange={(e) => setSimManual((m) => ({ ...m, voucher_code: e.target.value }))}
                placeholder="Test voucher code (optional)"
                data-testid="region-simulate-manual-voucher"
                className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 flex-1 min-w-[140px]"
              />
              <button
                type="button"
                onClick={() => runSimulation(simOpenFor)}
                disabled={simulating}
                data-testid="region-simulate-run-button"
                className="text-xs bg-teal-600 text-white px-3 py-1.5 rounded-lg disabled:opacity-50"
              >
                {simulating ? 'Running…' : 'Run Simulation'}
              </button>
              <button
                type="button"
                onClick={() => setSimOpenFor(null)}
                data-testid="region-simulate-cancel-button"
                className="text-xs text-slate-500 hover:text-slate-800"
              >
                Close
              </button>
            </div>
            {simResult?.manual_result && (
              <div className="bg-white border border-teal-200 rounded-lg p-2 text-xs" data-testid="region-simulate-manual-result">
                <span className="font-medium">{simResult.manual_result.name}</span> ({simResult.manual_result.email}) would be assigned to{' '}
                <span className="font-medium">{simResult.manual_result.would_assign_to || 'nobody (manual review)'}</span>
                {' '}<span className="text-slate-400">— {simResult.manual_result.reason}</span>
              </div>
            )}
            {simResult?.recent_leads && (
              <div className="space-y-1" data-testid="region-simulate-recent-leads">
                <p className="text-xs font-medium text-teal-800">Last {simResult.recent_leads.length} real lead(s) in this region:</p>
                {simResult.recent_leads.length === 0 && <p className="text-xs text-slate-400 italic">No leads in this region yet.</p>}
                {simResult.recent_leads.map((l) => (
                  <div key={l.lead_id} className="flex items-center justify-between gap-2 bg-white border border-slate-200 rounded-lg px-2 py-1.5 text-xs" data-testid={`region-simulate-lead-${l.lead_id}`}>
                    <span className="truncate flex-1">{l.name}</span>
                    <span className="text-slate-400">now: {l.currently_assigned_to || '—'}</span>
                    <span className="font-medium">→ {l.would_assign_to || 'unassigned'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {role === 'admin' && copyOpenFor && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 space-y-2" data-testid="region-copy-panel">
            <p className="text-xs font-medium text-indigo-800">
              Copy {regions.find((r) => r.region === copyOpenFor)?.name || copyOpenFor}'s auto-assign / voucher-required / owner settings to:
            </p>
            <div className="flex flex-wrap gap-3">
              {regions.filter((r) => r.region !== copyOpenFor).map((r) => (
                <label key={r.region} className="flex items-center gap-1.5 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    checked={copyTargets.includes(r.region)}
                    onChange={() => toggleCopyTarget(r.region)}
                    data-testid={`region-copy-target-${r.region}`}
                  />
                  {r.name}
                </label>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => applyCopy(copyOpenFor)}
                disabled={copying || copyTargets.length === 0}
                data-testid="region-copy-apply-button"
                className="text-xs bg-indigo-600 text-white px-3 py-1.5 rounded-lg disabled:opacity-50"
              >
                {copying ? 'Copying…' : `Copy to ${copyTargets.length || ''} region(s)`}
              </button>
              <button
                type="button"
                onClick={() => { setCopyOpenFor(null); setCopyTargets([]); }}
                data-testid="region-copy-cancel-button"
                className="text-xs text-slate-500 hover:text-slate-800"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </Section>
  );
};

export default RegionSettingsPanel;
